import { enumMembers, type ApiSchema } from "./apiSchema";
import { jsonBody, requestJson } from "./http/httpClient";
import { readArray, readBoolean, readInteger, readNullableInteger, readObject, readOneOf, readString, type JsonObject } from "./http/jsonDecoding";
import { decodePerson, type HistoryPerson } from "./people";

/**
 * A project's rules and the installation's settings (accounts.md ST-1): the
 * installation sets defaults and limits, each project chooses within them. Route:
 * `server/src/dmw/api/routes/accounts/project_settings.py`.
 */

export type SelfMerge = ApiSchema<"SelfMerge">;
/** In the order a choice lists them, from loosest to strictest. */
export const SELF_MERGE_VALUES = enumMembers<SelfMerge>({ allowed: true, only_without_other_curator: true, only_when_alone: true, never: true });
export type ProjectRuleName = ApiSchema<"ProjectRuleName">;
export const PROJECT_RULE_NAMES = enumMembers<ProjectRuleName>({ new_member_role: true, merge_rules: true, personal_skills_in_shared_threads: true, publish_to_researchspace: true, pinning_projects: true, release_roles: true, server_key_groups: true, installation_merges_system_changes: true });
export type InstallationSettingName = ApiSchema<"InstallationSettingName">;
const INSTALLATION_SETTING_NAMES = enumMembers<InstallationSettingName>({ members_create_projects: true, member_project_limit: true, creator_curates: true, new_projects: true, server_key_groups: true, deleted_project_restore_days: true, new_account_groups: true, access_requests: true, reasoning_levels: true });

/** The kind of change a merge rule is for; the server names the kinds (ADR-0028-D2). */
export type ChangeKind = string;

export interface MergeRule {
  readonly contributor: SelfMerge;
  readonly projectAdministrator: SelfMerge;
}

export interface ProjectRules {
  /** One rule per kind of change, in the server's order. */
  readonly mergeRules: Readonly<Record<ChangeKind, MergeRule>>;
  readonly personalSkillsInSharedThreads: boolean;
  /** F5: releases may go to the installation's ResearchSpace (knowledge-data.md K-13). */
  readonly publishToResearchSpace: boolean;
  /** JD-2: if the project is a library, the IDs of the projects that may pin it; `null`: every project. */
  readonly pinningProjects: readonly string[] | null;
  /** MA-C1: the installation merges its own system change; off, it waits for the curators (built provisionally). */
  readonly installationMergesSystemChanges: boolean;
}

export interface InstallationSettings {
  /** `null`: no limit. */
  readonly memberProjectLimit: number | null;
  readonly creatorCurates: boolean;
  /** The rules a new project starts with. */
  readonly newProjects: ProjectRules;
  /** F121: how many days administrators can restore a deleted project before it may be purged. */
  readonly deletedProjectRestoreDays: number;
  /** F133: the installation groups every new account joins once active. */
  readonly newAccountGroups: readonly string[];
  /** GS-8: people may ask for a permission they lack where they meet the refusal. */
  readonly accessRequests: boolean;
  /** The reasoning level per agent ID for Runs and people left at "Standard"; applied only where the agent offers it (module C). */
  readonly reasoningLevels: Readonly<Record<string, string>>;
}

export interface SettingsChange {
  /** `null` for a record DMW made itself: the first start, or a project older than rules. */
  readonly actor: HistoryPerson | null;
  /** ISO 8601. */
  readonly at: string;
  /** Empty for a first record. */
  readonly reason: string;
}

export interface ProjectSettings {
  readonly rules: ProjectRules;
  readonly lastChanges: Readonly<Partial<Record<ProjectRuleName, SettingsChange>>>;
  readonly installation: InstallationSettings;
  /** Every kind a curator assignment can name, as the server's modules register them. */
  readonly curatableItemKinds: readonly string[];
}

export interface InstallationSettingsRecord extends SettingsChange {
  readonly settings: InstallationSettings;
  readonly changed: readonly InstallationSettingName[];
}

export function readProjectSettings(projectId: string, signal?: AbortSignal): Promise<ProjectSettings> {
  return requestJson({ method: "GET", path: projectSettingsPath(projectId), signal, decodeResponse: decodeProjectSettings });
}

/** Replaces the project's rules; the reason is kept in the permission history. */
export function changeProjectRules(projectId: string, rules: ProjectRules, reason: string): Promise<ProjectSettings> {
  const body = { rules: encodeRules(rules), reason } satisfies ApiSchema<"ProjectSettingsChangeBody">;
  return requestJson({ method: "PUT", path: projectSettingsPath(projectId), body: jsonBody(body), decodeResponse: decodeProjectSettings });
}

/** The installation's settings records, oldest first; the last is in force (administrators). */
export function readInstallationSettings(signal?: AbortSignal): Promise<readonly InstallationSettingsRecord[]> {
  return requestJson({ method: "GET", path: INSTALLATION_SETTINGS_PATH, signal, decodeResponse: decodeRecords });
}

export function changeInstallationSettings(settings: InstallationSettings, reason: string): Promise<readonly InstallationSettingsRecord[]> {
  const body = { settings: encodeInstallation(settings), reason } satisfies ApiSchema<"InstallationSettingsChangeBody">;
  return requestJson({ method: "PUT", path: INSTALLATION_SETTINGS_PATH, body: jsonBody(body), decodeResponse: decodeRecords });
}

const INSTALLATION_SETTINGS_PATH = "/accounts/installation-settings";

function projectSettingsPath(projectId: string): `/${string}` {
  return `/projects/${encodeURIComponent(projectId)}/settings`;
}

function decodeProjectSettings(body: unknown): ProjectSettings {
  const settings = readObject(body, "project settings");
  const changes = readObject(settings["last_changes"], "last rule changes");
  return {
    rules: decodeRules(readObject(settings["rules"], "project rules")),
    lastChanges: Object.fromEntries(
      Object.entries(changes).map(([name, change]) => [readOneOf({ name }, "name", PROJECT_RULE_NAMES), decodeChange(readObject(change, "rule change"))]),
    ),
    installation: decodeInstallation(readObject(settings["installation"], "installation settings")),
    curatableItemKinds: readArray(settings["curatable_item_kinds"], "curatable item kinds", (kind) => readString({ kind }, "kind")),
  };
}

function decodeRecords(body: unknown): readonly InstallationSettingsRecord[] {
  return readArray(body, "installation settings records", (item) => {
    const record = readObject(item, "installation settings record");
    return {
      ...decodeChange(record),
      settings: decodeInstallation(readObject(record["settings"], "installation settings")),
      changed: readArray(record["changed"], "changed settings", (name) => readOneOf({ name }, "name", INSTALLATION_SETTING_NAMES)),
    };
  });
}

function decodeChange(change: JsonObject): SettingsChange {
  const actor = change["actor"];
  return {
    actor: actor === null || actor === undefined ? null : decodePerson(readObject(actor, "settings actor")),
    at: readString(change, "at"),
    reason: readString(change, "reason"),
  };
}

function decodeInstallation(settings: JsonObject): InstallationSettings {
  return {
    memberProjectLimit: readNullableInteger(settings, "member_project_limit"),
    creatorCurates: readBoolean(settings, "creator_curates"),
    newProjects: decodeRules(readObject(settings["new_projects"], "new project rules")),
    deletedProjectRestoreDays: readInteger(settings, "deleted_project_restore_days"),
    newAccountGroups: readArray(settings["new_account_groups"], "groups new accounts join", (group) => readString({ group }, "group")),
    accessRequests: readBoolean(settings, "access_requests"),
    reasoningLevels: Object.fromEntries(
      readArray(settings["reasoning_levels"], "reasoning levels", (item) => {
        const level = readObject(item, "reasoning level");
        return [readString(level, "agent_id"), readString(level, "effort_id")] as const;
      }),
    ),
  };
}

function decodeRules(rules: JsonObject): ProjectRules {
  const mergeRules = readObject(rules["merge_rules"], "merge rules");
  return {
    mergeRules: Object.fromEntries(
      Object.entries(mergeRules).map(([kind, rule]) => {
        const values = readObject(rule, "merge rule");
        return [kind, { contributor: readOneOf(values, "contributor", SELF_MERGE_VALUES), projectAdministrator: readOneOf(values, "project_administrator", SELF_MERGE_VALUES) }];
      }),
    ),
    personalSkillsInSharedThreads: readBoolean(rules, "personal_skills_in_shared_threads"),
    publishToResearchSpace: readBoolean(rules, "publish_to_researchspace"),
    pinningProjects: rules["pinning_projects"] === null ? null : readArray(rules["pinning_projects"], "pinning projects", (project) => readString({ project }, "project")),
    installationMergesSystemChanges: readBoolean(rules, "installation_merges_system_changes"),
  };
}

function encodeRules(rules: ProjectRules): ApiSchema<"ProjectRulesBody"> {
  return {
    merge_rules: Object.fromEntries(
      Object.entries(rules.mergeRules).map(([kind, rule]) => [kind, { contributor: rule.contributor, project_administrator: rule.projectAdministrator }]),
    ),
    personal_skills_in_shared_threads: rules.personalSkillsInSharedThreads,
    publish_to_researchspace: rules.publishToResearchSpace,
    pinning_projects: rules.pinningProjects === null ? null : [...rules.pinningProjects],
    installation_merges_system_changes: rules.installationMergesSystemChanges,
  };
}

function encodeInstallation(settings: InstallationSettings): ApiSchema<"InstallationSettingsValuesBody"> {
  return {
    member_project_limit: settings.memberProjectLimit,
    creator_curates: settings.creatorCurates,
    new_projects: encodeRules(settings.newProjects),
    deleted_project_restore_days: settings.deletedProjectRestoreDays,
    new_account_groups: [...settings.newAccountGroups],
    access_requests: settings.accessRequests,
    reasoning_levels: Object.entries(settings.reasoningLevels).map(([agentId, effortId]) => ({ agent_id: agentId, effort_id: effortId })),
  };
}
